import React, { FC } from 'react';
import {
  Text,
  VStack,
  useColorModeValue,
  Badge,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  HStack,
  Button,
  Avatar,
} from '@chakra-ui/react';
import { Survey, SurveyResponse } from './types';
import TextLabel from '../Misc/LText';
import { formatDatetime } from '../../util/string';
import LBadge from '../Misc/LBadge';
import SurveyResponseValue from './SurveyResponseValue';
import { FiCheck, FiTrash2 } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import { api, makeHeaders } from '../../util/api';
import { useFetch } from 'use-http';
import { useDialog } from '../../hooks/useDialog';
import { useNotify } from '../../hooks/useNotify';

interface SurveyResponsesProps {
  survey: Survey | null;
  response: SurveyResponse;
  disabled?: boolean;
  onChange?: () => void;
  displayStatus?: boolean;
}

const SurveyResponseCard: FC<SurveyResponsesProps> = ({
  response,
  survey,
  onChange,
  disabled = false,
  displayStatus = false,
}) => {
  const notify = useNotify();
  const {
    createDialog,
    closeDialog,
    setLoading
  } = useDialog();

  const {
    loading,
    patch,
    del,
    cache,
    response: requestResponse,
  } = useFetch(`${api}/surveys/${survey?.id}/responses/${response.id}`, makeHeaders());

  const archiveResponse = async () => {
    try {
      setLoading(true);
      cache.clear();
      await del();
      if (requestResponse.ok) {
        notify('Respuesta archivada', 'success');
        onChange && onChange();
      } else {
        throw new Error();
      }
    } catch {
      notify('Error al archivar respuesta', 'error');
    } finally {
      setLoading(false);
      closeDialog();
    }
  };

  const validateResponseHandler = async () => {
    try {
      setLoading(true);
      cache.clear();
      await patch('/validate');
      if (requestResponse.ok) {
        notify('Respuesta validada', 'success');
        closeDialog();
        onChange && onChange();
      } else {
        throw new Error();
      }
    } catch {
      notify('Error al validar respuesta', 'error');
    } finally {
      setLoading(false);
    }
  };

  const archiveResponseHandler = async () => {
    createDialog({
      title: 'Archivar respuesta',
      content: '¿Está seguro que desea archivar esta respuesta?',
      confirmText: 'Archivar',
      onConfirm: archiveResponse,
      onCancel: closeDialog,
    });
  };

  const borderColor = useColorModeValue('gray.200', 'gray.600');

  return (
    <VStack
      key={response.id}
      align="center"
      justify="space-between"
      border='1px'
      borderColor={borderColor}
      _first={{ borderTop: 'none' }}
      _notLast={{ borderBottom: 'none' }}
    >
      <Accordion allowToggle width='full'>
        <AccordionItem border='none'>
          <AccordionButton gap={2} cursor={disabled ? 'default' : 'pointer'}>
            <HStack justify='space-between' w='full'>
              <HStack>
                <Badge colorScheme='brand2' variant='subtle'>
                  <Text>{response.cic}</Text>
                </Badge>
                <TextLabel title={`${response.name} ${response.lastName}`} color='' />
              </HStack>
              <HStack>
              <TextLabel title={formatDatetime(response.createdAt)} color='' />
                {displayStatus && response.archived && (
                  <Badge colorScheme='gray'>
                    <Text>Archivada</Text>
                  </Badge>
                )}

                {displayStatus && !response.archived && (
                  <Badge colorScheme={response.validated ? 'green' : 'red'}>
                    <Text>{response.validated ? 'Validada' : 'No validada'}</Text>
                  </Badge>
                )}
              </HStack>
            </HStack>
          </AccordionButton>
          {!disabled && (
            <AccordionPanel>
              <VStack align='stretch' spacing={2} p={2}>
                {
                  survey && survey.content.questions.map((question, index) => (
                    <HStack key={question.id} justify='space-between'>
                      <HStack columnGap={3}>
                        <LBadge title={index + 1} />
                        <Text align='left' noOfLines={1}>{question.value}</Text>
                      </HStack>
                      <SurveyResponseValue
                        question={question}
                        answerValue={response.answers[question.id as any]}
                      />
                    </HStack>
                  ))
                }
                {!response.validated && (
                  <HStack justify='space-between' mt={8} align='end'>
                    <Link to={`/colaboradores/${response.createdBy.id}`}>
                      <HStack>
                        <Avatar size='xs' name={`${response.createdBy.firstName} ${response.createdBy.lastName}`} />
                        <Text fontSize='sm'>{`${response.createdBy.firstName} ${response.createdBy.lastName}`}</Text>
                      </HStack>
                    </Link>
                    <HStack>
                      <Button
                        leftIcon={<FiTrash2 />}
                        onClick={archiveResponseHandler}
                      >
                        Archivar respuesta
                      </Button>
                      <Button
                        leftIcon={<FiCheck />}
                        onClick={validateResponseHandler}
                        isLoading={loading}
                      >
                        Validar
                      </Button>
                    </HStack>
                  </HStack>
                )}
              </VStack>
            </AccordionPanel>
          )}
        </AccordionItem>
      </Accordion>
    </VStack>
  );
};

export default SurveyResponseCard;
